const SELECTORS = {
  section: 'section',
  radioInputs: 'input[type="radio"]',
  dataScript: ':scope > script[type="application/json"]',
  inventory: '.product-inventory-quantity',
  inventoryStatus: '[role="status"]',
  price: '.event-price',
  buyButton: '.event-buy-buttons__buy',
  waitlistButton: '.event-buy-buttons__waitlist',
  hiddenVariantInput: '.event-buy-buttons__form input[name="id"]',
  buyNowLink: '.event-buy-buttons__buy-now',
};

/**
 * @typedef {Object} EventProduct
 * @property {number} id
 * @property {number} variant_id
 * @property {boolean} available
 * @property {number} inventory_quantity
 * @property {string} price
 * @property {string} compare_at_price
 * @property {boolean} on_sale
 */

/**
 * @typedef {Object} EventI18n
 * @property {string} sold_out
 * @property {string} tickets_left
 * @property {string} per_ticket
 */

/**
 * @typedef {Object} EventData
 * @property {EventProduct[]} products
 * @property {EventI18n} i18n
 */

class EventProductPicker extends HTMLElement {
  constructor() {
    super();

    /** @param {Event} e */
    this._onChange = (e) => {
      const target = e.target;
      if (!(target instanceof HTMLInputElement) || target.type !== 'radio') return;
      this._onSelect(target);
    };
  }

  connectedCallback() {
    this._section = this.closest(SELECTORS.section);
    if (!this._section) return;

    const windowWithEventData = /** @type {Window & { eventData?: EventData }} */ (window);
    this._eventData = windowWithEventData.eventData;

    this.addEventListener('change', this._onChange);
  }

  disconnectedCallback() {
    this.removeEventListener('change', this._onChange);
  }

  /**
   * @param {HTMLInputElement} radio
   */
  _onSelect(radio) {
    if (!this._section || !this._eventData) return;

    this.querySelectorAll(SELECTORS.radioInputs).forEach((input) => {
      if (!(input instanceof HTMLInputElement)) return;
      input.dataset.previousChecked = input.dataset.currentChecked;
      input.dataset.currentChecked = String(input === radio);
    });

    const productId = Number(radio.dataset.productId);
    const product = this._eventData.products.find((/** @type {EventProduct} */ p) => p.id === productId);
    if (!product) return;

    this._updateInventory(product, this._eventData.i18n);
    this._updatePrice(product, this._eventData.i18n);
    this._updateBuyButtons(product);
  }

  /**
   * @param {EventProduct} product
   * @param {EventI18n} i18n
   */
  _updateInventory(product, i18n) {
    const el = this._section?.querySelector(SELECTORS.inventory);
    if (!(el instanceof HTMLElement)) return;

    el.dataset.productId = String(product.id);

    if (el.dataset.threshold === undefined) return;

    const threshold = Number(el.dataset.threshold);
    if (!Number.isFinite(threshold)) return;

    const hideInventory = product.inventory_quantity >= threshold;
    if (hideInventory) {
      el.hidden = true;
      return;
    }

    const span = el.querySelector(SELECTORS.inventoryStatus);
    if (!span) return;

    span.textContent =
      product.inventory_quantity <= 0
        ? i18n.sold_out
        : `${product.inventory_quantity} ${i18n.tickets_left}`;

    el.hidden = false;
  }

  /**
   * @param {EventProduct} product
   * @param {EventI18n} i18n
   */
  _updatePrice(product, i18n) {
    const el = this._section?.querySelector(SELECTORS.price);
    if (!el) return;

    let html;
    if (product.on_sale) {
      html =
        `<span class="event-price__compare compare-at-price event-price__amount--large">${product.compare_at_price}</span>` +
        `<span class="event-price__amount price price--sale event-price__amount--large">${product.price} ${i18n.per_ticket}</span>`;
    } else {
      html = `<span class="event-price__amount event-price__amount--large">${product.price} ${i18n.per_ticket}</span>`;
    }

    el.innerHTML = html;
  }

  /**
   * @param {EventProduct} product
   */
  _updateBuyButtons(product) {
    const buyEl = this._section?.querySelector(SELECTORS.buyButton);
    const waitlistEl = this._section?.querySelector(SELECTORS.waitlistButton);

    if (buyEl instanceof HTMLElement) buyEl.hidden = !product.available;
    if (waitlistEl instanceof HTMLElement) waitlistEl.hidden = product.available;

    const hiddenInput = this._section?.querySelector(SELECTORS.hiddenVariantInput);
    if (hiddenInput instanceof HTMLInputElement) hiddenInput.value = String(product.variant_id);

    const buyNowLink = this._section?.querySelector(SELECTORS.buyNowLink);
    if (buyNowLink instanceof HTMLAnchorElement) {
      buyNowLink.href = `/cart/${product.variant_id}:1?checkout`;
    }
  }
}

customElements.define('event-product-picker', EventProductPicker);
